<template>
  <div>
    <p class="member-title fs-15 mb-2">Solicitante</p>
    <div class="mb-3">
      <NameInitialsIcon :user="drawingRequest.requester || {}" color="#163D7D" />
    </div>

    <div v-for="(config, role, index) in roles" :key="role" :class="{ 'mb-3': index === 0 }">
      <div class="d-flex justify-content-between align-items-center mb-2" style="min-height: 31px">
        <p class="member-title fs-15 mb-0">{{ config.label }}</p>
        <b-button-group v-if="permissions.can_manage_members" size="sm">
          <b-button variant="outline-secondary" size="sm" @click="openModal(role)">
            {{ drawingRequest[role] ? 'Cambiar' : 'Asignar' }}
          </b-button>
          <b-button v-if="drawingRequest[role]" variant="outline-danger" size="sm" @click="openRemoveModal(role)">
            Quitar
          </b-button>
        </b-button-group>
      </div>

      <NameInitialsIcon v-if="drawingRequest[role]" :user="drawingRequest[role]" :color="config.color" />
      <div v-else class="d-flex align-items-center">
        <span
          class="rounded-circle d-flex align-items-center justify-content-center text-secondary flex-shrink-0"
          style="width: 37px; height: 37px; border: 1px dashed #8a939d; margin-right: 10px"
        >?</span>
        <span class="text-secondary">Sin asignar</span>
      </div>
    </div>

    <Modal v-if="modalRole && permissions.can_manage_members" contentWidth="700px" :required="true" @close="closeModal">
      <template #header>
        <p class="fs-20 font-weight-bold ml-1 mb-0" style="line-height: 0.675rem; color: #163D7D;">
          {{ modalTitle }}
        </p>
      </template>

      <template #body>
        <p class="mb-1 text-muted">{{ roleLabel }}<span class="text-danger ml-1">*</span></p>

        <b-input-group v-if="selectedUser">
          <b-input :value="selectedUser | concatName3 | titleize" disabled :state="true" />
          <b-input-group-append>
            <b-button variant="outline-secondary" @click="selectedUser = null">Cambiar</b-button>
          </b-input-group-append>
        </b-input-group>

        <div v-else>
          <b-input-group>
            <b-input
              v-model="searchTerm"
              autofocus
              :placeholder="isDrafter ? 'Filtrar por nombre o correo' : 'Buscar por nombre, apellido o correo'"
              @keyup.enter="onSearchEnter"
            />
            <b-input-group-append>
              <b-button
                v-if="!isDrafter"
                size="sm"
                variant="outline-secondary"
                :disabled="!searchTerm.trim()"
                @click="searchApprovers"
              >
                <font-awesome-icon :icon="['fas', 'search']" fixed-width />
              </b-button>
              <b-button variant="outline-secondary" class="ml-2" size="sm" :disabled="!searchTerm" @click="clearSearch">
                <font-awesome-icon :icon="['far', 'undo-alt']" fixed-width />
                <span class="pl-1">Limpiar</span>
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <div style="max-height: 250px" class="overflow-auto mt-1">
            <b-table
              :items="tableItems"
              :fields="searchFields"
              outlined
              show-empty
              class="fs-14"
              :tbody-tr-class="rowClass"
              @row-clicked="selectUser"
            >
              <template #empty>
                <div class="text-center">
                  <span v-if="searching" class="text-info">
                    <font-awesome-icon :icon="['fas', 'spinner']" pulse fixed-width />
                    <span class="pl-2">{{ isDrafter ? 'Cargando dibujantes...' : 'Buscando...' }}</span>
                  </span>
                  <span v-else-if="searchFailed" class="text-danger">
                    Hubo un error al cargar los usuarios, intente de nuevo
                  </span>
                  <span v-else-if="searchDone" class="text-danger">
                    {{ isDrafter && !searchTerm ? 'No hay dibujantes disponibles' : 'No hay resultados' }}
                  </span>
                  <span v-else class="text-secondary">
                    Buscar por nombre, apellido o correo
                  </span>
                </div>
              </template>
              <template #cell(name)="data">
                <span>{{ data.item | concatName3 | titleize }}</span>
              </template>
            </b-table>
          </div>
        </div>

        <p v-if="errorMessage" class="text-danger fs-13 mt-2 mb-0">{{ errorMessage }}</p>
      </template>

      <template #footer>
        <b-button variant="outline-danger" @click="closeModal">Cerrar</b-button>
        <b-button variant="outline-success" :disabled="!canSave" @click="save">
          <b-spinner v-if="processing" small class="mr-1" />
          Actualizar
        </b-button>
      </template>
    </Modal>

    <ConfirmModal
      v-if="removeRole && permissions.can_manage_members"
      :required="true"
      :disabled="removing"
      @close="closeRemoveModal"
      @onConfirm="removeMember"
    >
      <template #header>
        <h5 class="mb-0">Quitar {{ removeRoleLabel }}</h5>
      </template>
      <template #body>
        <font-awesome-icon :icon="['far', 'exclamation-triangle']" size="2x" class="text-warning mb-3" fixed-width />

        <p class="fs-16 mb-2">
          ¿Desea quitar a <strong>{{ removeUserName }}</strong> como {{ removeRoleLabel }} de esta solicitud?
        </p>

        <p v-if="removing" class="text-info fs-14 mt-3 mb-0">
          <b-spinner small class="mr-1" />
          Quitando...
        </p>
        <b-alert v-if="removeError" show variant="danger" class="fs-14 mt-3 mb-0 py-2">
          {{ removeError }}
        </b-alert>
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import Modal from '../../shared/Modal'
import ConfirmModal from '../../shared/ConfirmModal'
import NameInitialsIcon from '../../shared/NameInitialsIcon.vue'

const ROLES = {
  drafter: { label: 'Dibujante', field: 'drafter_id', color: '#ffa500' },
  approver: { label: 'Aprobador', field: 'approver_id', color: '#3b8b9e' }
}

export default {
  name: 'DrawingRequestShowMembers',
  components: { Modal, ConfirmModal, NameInitialsIcon },
  props: {
    drawingRequest: { type: Object, required: true },
    permissions: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      roles: ROLES,
      modalRole: null,
      selectedUser: null,
      searchTerm: '',
      users: [],
      searching: false,
      searchDone: false,
      searchFailed: false,
      processing: false,
      errorMessage: null,
      removeRole: null,
      removing: false,
      removeError: null,
      searchFields: [
        { key: 'name', label: 'Nombre', thClass: 'text-muted font-lato' },
        { key: 'email', label: 'Correo', thClass: 'text-muted font-lato', thStyle: { width: '240px' } }
      ]
    }
  },
  computed: {
    isDrafter () {
      return this.modalRole === 'drafter'
    },
    roleLabel () {
      return this.modalRole ? ROLES[this.modalRole].label : ''
    },
    modalTitle () {
      const action = this.drawingRequest[this.modalRole] ? 'Cambiar' : 'Asignar'
      return `${action} ${this.roleLabel.toLowerCase()}`
    },
    currentUserIdForRole () {
      const user = this.modalRole && this.drawingRequest[this.modalRole]
      return user ? user.id : null
    },
    tableItems () {
      if (!this.isDrafter) return this.users

      const term = this.normalizeString(this.searchTerm)
      if (!term) return this.users

      return this.users.filter(user => {
        const text = this.normalizeString(`${user.first_name} ${user.last_name || ''} ${user.email || ''}`)
        return text.includes(term)
      })
    },
    canSave () {
      return !this.processing && !!this.selectedUser && this.selectedUser.id !== this.currentUserIdForRole
    },
    removeRoleLabel () {
      return this.removeRole ? ROLES[this.removeRole].label.toLowerCase() : ''
    },
    removeUserName () {
      const user = this.removeRole && this.drawingRequest[this.removeRole]
      return user ? `${user.first_name} ${user.last_name || ''}`.trim() : ''
    }
  },
  methods: {
    openModal (role) {
      this.modalRole = role
      this.resetModal()
      if (role === 'drafter') this.fetchDrafters()
    },
    closeModal () {
      this.modalRole = null
      this.resetModal()
    },
    resetModal () {
      this.selectedUser = null
      this.searchTerm = ''
      this.users = []
      this.searchDone = false
      this.searchFailed = false
      this.errorMessage = null
    },
    clearSearch () {
      this.searchTerm = ''
      if (!this.isDrafter) {
        this.users = []
        this.searchDone = false
      }
    },
    onSearchEnter () {
      if (!this.isDrafter) this.searchApprovers()
    },
    fetchDrafters () {
      this.requestUsers('/api/production/drawing_requests/drafters')
    },
    searchApprovers () {
      if (!this.searchTerm.trim()) return
      this.requestUsers('/api/production/drawing_requests/search_approvers', {
        q: this.normalizeString(this.searchTerm)
      })
    },
    requestUsers (url, params = {}) {
      if (this.searching) return
      this.searching = true
      this.searchFailed = false
      this.searchDone = false

      this.$http.get(url, { params })
        .then((res) => {
          this.users = res.data
          this.searchDone = true
        })
        .catch(() => {
          this.searchFailed = true
          this.users = []
        })
        .finally(() => {
          this.searching = false
        })
    },
    selectUser (user) {
      if (user.id === this.currentUserIdForRole) return
      this.selectedUser = user
    },
    rowClass (item) {
      if (!item) return
      return item.id === this.currentUserIdForRole ? 'table-row-muted' : 'clickable'
    },
    save () {
      if (!this.canSave) return
      this.processing = true
      this.errorMessage = null

      this.updateMember(this.modalRole, this.selectedUser.id)
        .then(() => this.closeModal())
        .catch((message) => {
          this.errorMessage = message || 'No se pudo guardar el cambio'
        })
        .finally(() => {
          this.processing = false
        })
    },
    openRemoveModal (role) {
      this.removeRole = role
      this.removeError = null
    },
    closeRemoveModal () {
      if (this.removing) return
      this.removeRole = null
      this.removeError = null
    },
    removeMember () {
      if (this.removing) return
      this.removing = true
      this.removeError = null

      this.updateMember(this.removeRole, null)
        .then(() => {
          this.removing = false
          this.closeRemoveModal()
        })
        .catch((message) => {
          this.removeError = message || `No se pudo quitar el ${ROLES[this.removeRole].label.toLowerCase()}`
          this.removing = false
        })
    },
    updateMember (role, userId) {
      return this.$http.patch(`/api/production/drawing_requests/${this.drawingRequest.id}`, {
        drawing_request: { [ROLES[role].field]: userId }
      })
        .then((res) => {
          this.$emit('updated', res.data)
        })
        .catch((error) => {
          const data = (error.response && error.response.data) || {}
          return Promise.reject(data.error || data.message)
        })
    },
    normalizeString (str) {
      return (str || '').toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/[áÁ]/g, 'a')
        .replace(/[éÉ]/g, 'e')
        .replace(/[íÍ]/g, 'i')
        .replace(/[óÓ]/g, 'o')
        .replace(/[úÚ]/g, 'u')
    }
  }
}
</script>

<style scoped>
.member-title {
  font-weight: 500;
  color: #868c91;
}
</style>
